// Shared keg scan UI used by Transfers (and same pattern as Invoice / packaging).
import React, { useState } from 'react';
import { isValidKegCode, normalizeKegCode } from '../utils/kegCode';
import KegScanOverlay from './KegScanOverlay';
import { primeScanAudio } from '../utils/scanFeedback';

export interface KegScanPanelProps {
  title: string;
  codes: string[];
  /** When set, shows progress like "3 of 6 scanned". */
  requiredCount?: number;
  onAdd: (code: string) => void | Promise<void>;
  onRemove?: (code: string) => void;
  onDone: () => void;
  error?: string | null;
  /** Larger camera-friendly input (Transfers / Taproom mobile). */
  largeInput?: boolean;
}

/**
 * Manual entry + camera scan panel for keg serials.
 * Parent owns validation (exists / Filled / product / location) via onAdd.
 */
const KegScanPanel: React.FC<KegScanPanelProps> = ({
  title,
  codes,
  requiredCount,
  onAdd,
  onRemove,
  onDone,
  error,
  largeInput = false,
}) => {
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const submit = async (raw: string) => {
    const code = normalizeKegCode(raw);
    if (!isValidKegCode(code)) {
      setLocalError('Valid keg tag needed (e.g., EK09503)');
      return;
    }
    setBusy(true);
    setLocalError(null);
    try {
      await onAdd(code);
      setInput('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add keg';
      setLocalError(msg);
    } finally {
      setBusy(false);
    }
  };

  const displayError = localError || error;
  const done = requiredCount != null && codes.length >= requiredCount;

  return (
    <div className="keg-scan-panel flow-card" data-testid="keg-scan-panel" style={{ marginTop: 16 }}>
      <h4 className="flow-section-title" style={{ marginTop: 0 }}>
        {title}
      </h4>
      {requiredCount != null && (
        <p
          className={`flow-scan-count ${done ? 'complete' : ''}`}
          data-testid="keg-scan-progress"
        >
          {codes.length} of {requiredCount} scanned
          {done ? ' — complete' : ''}
        </p>
      )}
      {displayError && (
        <div className="error" style={{ marginBottom: 8 }}>
          {displayError}
        </div>
      )}
      <div className="flow-field">
        <label htmlFor="keg-scan-input">Keg code</label>
        <input
          id="keg-scan-input"
          className={largeInput ? 'flow-scan-input' : undefined}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit(input);
            }
          }}
          placeholder="Scan or type keg code"
          data-testid="keg-scan-input"
          disabled={busy || done}
          autoComplete="off"
          inputMode="text"
          autoCapitalize="characters"
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => void submit(input)}
          disabled={busy || done}
          style={{ flex: 1, minHeight: 48 }}
        >
          Add
        </button>
        <button type="button" onClick={onDone} style={{ flex: 1, minHeight: 48, background: '#EEE', color: '#333' }}>
          Done
        </button>
      </div>
      {codes.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }} data-testid="keg-scan-list">
          {codes.map((c) => (
            <li
              key={c}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                marginBottom: 8,
                minHeight: 44,
              }}
            >
              <code style={{ fontSize: 16 }}>{c}</code>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(c)}
                  aria-label={`Remove ${c}`}
                  style={{ minHeight: 44, background: '#ffe6e6', color: '#a00' }}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        data-testid="keg-scan-camera"
        onClick={() => {
          primeScanAudio(); // iOS: sound needs this tap
          setLocalError(null);
          setCameraOpen(true);
        }}
        style={{ width: '100%', minHeight: 52 }}
      >
        Scan with camera
      </button>
      {cameraOpen && (
        <KegScanOverlay
          title={title}
          codes={codes}
          target={requiredCount}
          onScan={async (code) => {
            await onAdd(code);
          }}
          onRemove={onRemove}
          onDone={() => setCameraOpen(false)}
        />
      )}
    </div>
  );
};

export default KegScanPanel;
